import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, MonitorIcon, CodeIcon, ServerIcon, MapPinIcon, HammerIcon, HomeIcon, HeartPulseIcon, UtensilsIcon, UsersIcon, StoreIcon, BriefcaseIcon } from 'lucide-react';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const [isRegioOpen, setIsRegioOpen] = useState(false);
  // Track active route
  useEffect(() => {
    const handleHashChange = () => {
      setActiveHash(window.location.hash || '#/');
    };
    // Set initial
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  // Scroll detection for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const currentHash = window.location.hash;
    if (currentHash !== '#/' && currentHash !== '') {
      window.location.hash = '#/';
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    } else {
      document.getElementById('contact')?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  const services = [{
    label: 'SEO voor websites',
    href: '#/seo-voor-websites',
    icon: MonitorIcon,
    description: 'Hoger scoren in Google'
  }, {
    label: 'WordPress websites',
    href: '#/wordpress-websites',
    icon: CodeIcon,
    description: 'Flexibel en makkelijk beheerbaar'
  }, {
    label: 'Hosting & onderhoud',
    href: '#/hosting-onderhoud',
    icon: ServerIcon,
    description: 'Snel, veilig en up-to-date'
  }];
  const sectors = [{
    label: 'Bouw & Installatie',
    href: '#/sector/bouw-installatie',
    icon: HammerIcon
  }, {
    label: 'Makelaars & Vastgoed',
    href: '#/sector/makelaars-vastgoed',
    icon: HomeIcon
  }, {
    label: 'Zorgpraktijken',
    href: '#/sector/zorgpraktijken',
    icon: HeartPulseIcon
  }, {
    label: 'Horeca & Hospitality',
    href: '#/sector/horeca-hospitality',
    icon: UtensilsIcon
  }, {
    label: 'Coaches & Consultants',
    href: '#/sector/coaches-consultants',
    icon: UsersIcon
  }, {
    label: 'Lokale Retail',
    href: '#/sector/lokale-retail',
    icon: StoreIcon
  }, {
    label: 'Zakelijke Dienstverlening',
    href: '#/sector/zakelijke-dienstverlening',
    icon: BriefcaseIcon
  }];
  const localPages = [{
    label: 'Webdesign Alkmaar',
    href: '#/webdesign-alkmaar'
  }, {
    label: 'Webdesign Haarlem',
    href: '#/webdesign-haarlem'
  }, {
    label: 'Webdesign Amsterdam',
    href: '#/webdesign-amsterdam'
  }, {
    label: 'Webdesign Zaanstad',
    href: '#/webdesign-zaanstad'
  }, {
    label: 'Webdesign Purmerend',
    href: '#/webdesign-purmerend'
  }, {
    label: 'Webdesign Hoorn',
    href: '#/webdesign-hoorn'
  }, {
    label: 'Webdesign Den Helder',
    href: '#/webdesign-den-helder'
  }, {
    label: 'Webdesign Hilversum',
    href: '#/webdesign-hilversum'
  }];
  const isServiceActive = services.some(s => s.href === activeHash);
  const isSectorActive = sectors.some(s => activeHash.startsWith(s.href));
  const isLocalActive = localPages.some(p => p.href === activeHash);
  return <header className="fixed top-0 left-0 right-0 z-50" data-id="element-486">
      <div className={`bg-white transition-shadow duration-300 ${isScrolled || isMobileMenuOpen ? 'shadow-medium' : ''}`} data-id="element-487">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-488">
          <div className="flex items-center justify-between h-16 lg:h-[72px]" data-id="element-489">
            {/* Logo */}
            <a href="#/" className="flex-shrink-0" onClick={closeMobileMenu} data-id="element-490">
              <img src="https://cdn.magicpatterns.com/uploads/fGPZ5CSfvu5NW1orNYB6mv/Logo_Noord_Hooft.svg" alt="Noord" className="h-8 lg:h-9 w-auto" data-id="element-491" />
            </a>

            {/* Centered Navigation — Desktop */}
            <nav className="hidden md:flex items-center gap-1 lg:gap-3" data-id="element-492">
              <a href="#/" className={`px-3 py-2 text-sm font-medium transition-colors rounded-lg ${activeHash === '#/' ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-493">
                Home
              </a>

              {/* Diensten Dropdown */}
              <div className="relative group" data-id="element-494">
                <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${isServiceActive ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-495">
                  Diensten
                  <ChevronDownIcon className="w-4 h-4 text-navy-400 group-hover:text-navy-900 transition-colors" data-id="element-496" />
                </button>

                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50" data-id="element-497">
                  <div className="bg-white rounded-xl shadow-medium border border-navy-100 py-2 w-64" data-id="element-498">
                    {services.map(service => <a key={service.label} href={service.href} className={`flex items-start gap-3 px-4 py-3 hover:bg-navy-50 transition-colors ${activeHash === service.href ? 'bg-navy-50/50' : ''}`} data-id="element-499">
                        <div className="mt-0.5 w-8 h-8 rounded-lg bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-500">
                          <service.icon className="w-4 h-4 text-accent" data-id="element-501" />
                        </div>
                        <div data-id="element-502">
                          <div className={`text-sm font-semibold ${activeHash === service.href ? 'text-accent' : 'text-navy-900'}`} data-id="element-503">
                            {service.label}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5" data-id="element-504">
                            {service.description}
                          </div>
                        </div>
                      </a>)}
                  </div>
                </div>
              </div>

              {/* Sectoren Dropdown */}
              <div className="relative group" data-id="element-505">
                <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${isSectorActive ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-506">
                  Sectoren
                  <ChevronDownIcon className="w-4 h-4 text-navy-400 group-hover:text-navy-900 transition-colors" data-id="element-507" />
                </button>

                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50" data-id="element-508">
                  <div className="bg-white rounded-xl shadow-medium border border-navy-100 py-2 w-64" data-id="element-509">
                    {sectors.map(sector => <a key={sector.label} href={sector.href} className={`flex items-center gap-3 px-4 py-3 hover:bg-navy-50 transition-colors ${activeHash.startsWith(sector.href) ? 'bg-navy-50/50' : ''}`} data-id="element-510">
                        <sector.icon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-511" />
                        <span className={`text-sm font-medium ${activeHash.startsWith(sector.href) ? 'text-accent' : 'text-navy-900'}`} data-id="element-512">
                          {sector.label}
                        </span>
                      </a>)}
                  </div>
                </div>
              </div>

              {/* Regio Dropdown */}
              <div className="relative group" data-id="element-513">
                <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-lg ${isLocalActive ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-514">
                  Regio
                  <ChevronDownIcon className="w-4 h-4 text-navy-400 group-hover:text-navy-900 transition-colors" data-id="element-515" />
                </button>

                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50" data-id="element-516">
                  <div className="bg-white rounded-xl shadow-medium border border-navy-100 py-2 w-56" data-id="element-517">
                    {localPages.map(page => <a key={page.label} href={page.href} className={`flex items-center gap-3 px-4 py-3 hover:bg-navy-50 transition-colors ${activeHash === page.href ? 'bg-navy-50/50' : ''}`} data-id="element-518">
                        <MapPinIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-519" />
                        <span className={`text-sm font-medium ${activeHash === page.href ? 'text-accent' : 'text-navy-900'}`} data-id="element-520">
                          {page.label}
                        </span>
                      </a>)}
                  </div>
                </div>
              </div>

              <a href="#/kennisbank" className={`px-3 py-2 text-sm font-medium transition-colors rounded-lg ${activeHash === '#/kennisbank' ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-521">
                Kennisbank
              </a>
              <a href="#/portfolio" className={`px-3 py-2 text-sm font-medium transition-colors rounded-lg ${activeHash === '#/portfolio' ? 'text-navy-900 underline underline-offset-4 decoration-2 decoration-navy-900' : 'text-navy-600 hover:text-navy-900'}`} data-id="element-522">
                Portfolio
              </a>
            </nav>

            <div className="flex items-center gap-3" data-id="element-523">
              <a href="#contact" onClick={scrollToContact} className="hidden md:inline-flex bg-navy-900 hover:bg-navy-800 text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-200" data-id="element-524">
                Contact
              </a>

              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-navy-900 hover:bg-navy-50 transition-colors" aria-label={isMobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-525">
                {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-526" /> : <MenuIcon className="w-6 h-6" data-id="element-527" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Blue gradient bottom border */}
      <div className="h-1" style={{
      background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 50%, #3B82F6 100%)'
    }} data-id="element-528" />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="md:hidden fixed inset-0 top-[68px] bg-black/20 z-40" onClick={() => setIsMobileMenuOpen(false)} data-id="element-529" />}

      {/* Mobile Menu Panel */}
      <div className={`md:hidden fixed top-[68px] left-0 right-0 bg-white z-50 transition-all duration-300 ease-in-out overflow-y-auto max-h-[calc(100vh-68px)] ${isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`} style={{
      boxShadow: isMobileMenuOpen ? '0 8px 32px rgba(11, 29, 58, 0.12)' : 'none'
    }} data-id="element-530">
        <div className="px-5 py-4" data-id="element-531">
          <nav className="flex flex-col" data-id="element-532">
            <a href="#/" onClick={closeMobileMenu} className={`py-4 text-base font-medium transition-colors border-b border-navy-100 ${activeHash === '#/' ? 'text-accent' : 'text-navy-900'}`} data-id="element-533">
              Home
            </a>

            {/* Mobile Services Accordion */}
            <div className="border-b border-navy-100" data-id="element-534">
              <button onClick={() => setIsServicesOpen(!isServicesOpen)} className="w-full flex items-center justify-between py-4 text-base font-medium text-navy-900" data-id="element-535">
                <span className={isServiceActive ? 'text-accent' : ''} data-id="element-536">
                  Diensten
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-navy-400 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} data-id="element-537" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${isServicesOpen ? 'max-h-64 pb-4' : 'max-h-0'}`} data-id="element-538">
                <div className="flex flex-col gap-1 pl-4 border-l-2 border-navy-100 ml-2" data-id="element-539">
                  {services.map(service => <a key={service.label} href={service.href} onClick={closeMobileMenu} className={`py-3 px-4 text-sm font-medium rounded-lg transition-colors ${activeHash === service.href ? 'bg-accent-light/50 text-accent' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-540">
                      {service.label}
                    </a>)}
                </div>
              </div>
            </div>

            {/* Mobile Sectors Accordion */}
            <div className="border-b border-navy-100" data-id="element-541">
              <button onClick={() => setIsSectorsOpen(!isSectorsOpen)} className="w-full flex items-center justify-between py-4 text-base font-medium text-navy-900" data-id="element-542">
                <span className={isSectorActive ? 'text-accent' : ''} data-id="element-543">
                  Sectoren
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-navy-400 transition-transform duration-200 ${isSectorsOpen ? 'rotate-180' : ''}`} data-id="element-544" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${isSectorsOpen ? 'max-h-96 pb-4' : 'max-h-0'}`} data-id="element-545">
                <div className="flex flex-col gap-1 pl-4 border-l-2 border-navy-100 ml-2" data-id="element-546">
                  {sectors.map(sector => <a key={sector.label} href={sector.href} onClick={closeMobileMenu} className={`py-3 px-4 text-sm font-medium rounded-lg transition-colors ${activeHash.startsWith(sector.href) ? 'bg-accent-light/50 text-accent' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-547">
                      {sector.label}
                    </a>)}
                </div>
              </div>
            </div>

            {/* Mobile Regio Accordion */}
            <div className="border-b border-navy-100" data-id="element-548">
              <button onClick={() => setIsRegioOpen(!isRegioOpen)} className="w-full flex items-center justify-between py-4 text-base font-medium text-navy-900" data-id="element-549">
                <span className={isLocalActive ? 'text-accent' : ''} data-id="element-550">
                  Regio
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-navy-400 transition-transform duration-200 ${isRegioOpen ? 'rotate-180' : ''}`} data-id="element-551" />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${isRegioOpen ? 'max-h-80 pb-4' : 'max-h-0'}`} data-id="element-552">
                <div className="flex flex-col gap-1 pl-4 border-l-2 border-navy-100 ml-2" data-id="element-553">
                  {localPages.map(page => <a key={page.label} href={page.href} onClick={closeMobileMenu} className={`py-3 px-4 text-sm font-medium rounded-lg transition-colors ${activeHash === page.href ? 'bg-accent-light/50 text-accent' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-554">
                      {page.label}
                    </a>)}
                </div>
              </div>
            </div>

            <a href="#/kennisbank" onClick={closeMobileMenu} className={`py-4 text-base font-medium transition-colors border-b border-navy-100 ${activeHash === '#/kennisbank' ? 'text-accent' : 'text-navy-900'}`} data-id="element-555">
              Kennisbank
            </a>
            <a href="#/portfolio" onClick={closeMobileMenu} className={`py-4 text-base font-medium transition-colors border-b border-navy-100 ${activeHash === '#/portfolio' ? 'text-accent' : 'text-navy-900'}`} data-id="element-556">
              Portfolio
            </a>
          </nav>

          <a href="#contact" onClick={scrollToContact} className="flex items-center justify-center w-full mt-6 bg-accent hover:bg-accent-hover text-white font-semibold py-4 rounded-xl text-base transition-all duration-200" data-id="element-557">
            Neem contact op
          </a>
        </div>
      </div>
    </header>;
}